/**
 * Wire shapes for the SLC dashboard API.
 *
 * Product rule that shapes this whole file: SLC stores exactly ONE record per unique
 * normalized URL, owned permanently by the FIRST share. There is no share count, no list of
 * later sharers, no channel history. If you find yourself wanting `shareCount`, the answer is
 * that it does not exist by design.
 */

export const PROCESSING_STATUSES = [
  'RESERVED',
  'ENRICHING',
  'ENRICHED',
  'SCREENSHOT_PENDING',
  'COMPLETE',
  'FAILED',
  'DUPLICATE',
  'BLOCKED',
] as const;

export type ProcessingStatus = (typeof PROCESSING_STATUSES)[number];

export const SCREENSHOT_STATUSES = [
  'PENDING',
  'PROCESSING',
  'COMPLETE',
  'FAILED',
  'BLOCKED',
] as const;

export type ScreenshotStatus = (typeof SCREENSHOT_STATUSES)[number];

/** `anonymous` is a visitor with no session — the catalogue is readable without one. */
export type Role = 'anonymous' | 'viewer' | 'admin';

export interface CurrentUser {
  sub: string;
  email: string;
  name?: string;
  role: Role;
}

export interface AddLinkBody {
  url: string;
}

/**
 * `added`: a new record, credited to the caller and queued for enrichment. `existing`: first link
 * wins — `link` is the record that already owns this URL (absent only for a share made a moment
 * earlier whose record is not readable yet).
 */
export interface AddLinkResult {
  outcome: 'added' | 'existing';
  link?: LinkSummary;
}

/** `public`: anyone can browse the catalogue. `private`: signing in is required. */
export type CatalogueVisibility = 'public' | 'private';

/** Runtime settings an administrator changes from the Settings page. */
export interface AppSettings {
  catalogueVisibility: CatalogueVisibility;
  /** Slack channels whose images and videos are collected. Empty: none are. */
  mediaChannelIds: string[];
  updatedAt?: string;
  /** Email of the administrator who last changed them. */
  updatedBy?: string;
}

/** A partial update: only the fields sent are changed. */
export interface UpdateSettingsBody {
  catalogueVisibility?: CatalogueVisibility;
  mediaChannelIds?: string[];
}

/** A channel the Settings page offers for media collection. */
export interface MediaChannelOption {
  /** Slack channel id. */
  value: string;
  /** `#name` when known, else the id. */
  label: string;
  monitored: boolean;
  /** Distinct images and videos stored from this channel. */
  mediaCount: number;
  /** Distinct links first shared here. */
  linkCount: number;
  /** ISO-8601: the last time an image or video was posted here, monitored or not. */
  lastMediaAt?: string;
}

export type MediaKind = 'image' | 'video';

/**
 * An image or video collected from a monitored Slack channel. One per DISTINCT file, owned by
 * whoever shared it first — the same rule as links. Only ever served to signed-in users.
 */
export interface MediaItem {
  mediaId: string;
  kind: MediaKind;
  name?: string;
  title?: string;
  /** One or two sentences on what it shows. */
  description?: string;
  /**
   * Who wrote the title and description: `generated` by the model, `admin` by hand. Absent while
   * the title is still the one it was uploaded with.
   */
  textSource?: MediaTextSource;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  /** Videos only, when Slack measured it. */
  durationMs?: number;
  /** Presigned URL of the original, valid for about an hour. */
  url?: string;
  /** Presigned URL of a still: a thumbnail, or a video's poster frame. May be absent for a video. */
  thumbnailUrl?: string;
  firstShare: FirstShare;
  createdAt: string;
}

export type MediaTextSource = 'generated' | 'admin';

export interface UpdateMediaBody {
  title?: string;
  /** An empty string removes it. */
  description?: string;
}

export interface MediaFacetOption {
  value: string;
  label: string;
  /** Distinct images and videos. */
  mediaCount: number;
}

export interface MediaFacets {
  channels: MediaFacetOption[];
  sharers: MediaFacetOption[];
}

export interface ListMediaParams {
  channelId?: string;
  sharerId?: string;
  kind?: MediaKind;
  limit?: number;
  cursor?: string;
}

/** Slack provenance of the FIRST accepted share. Immutable. */
export interface FirstShare {
  /**
   * Absent means Slack. `dashboard`: added by a signed-in user on the dashboard — no channel,
   * message or permalink, and the user fields describe whoever added it.
   */
  source?: 'slack' | 'dashboard';
  slackTeamId: string;
  slackChannelId: string;
  slackChannelName?: string;
  slackUserId: string;
  slackUserDisplayName?: string;
  slackUserRealName?: string;
  slackUserEmail?: string;
  slackMessageTs: string;
  slackThreadTs?: string;
  permalink?: string;
  /** ISO-8601 */
  sharedAt: string;
}

export interface LinkSummary {
  linkId: string;
  originalUrl: string;
  domain: string;
  title?: string;
  description?: string;
  siteName?: string;
  /** Category slugs. */
  categories: string[];
  faviconUrl?: string;
  ogImageUrl?: string;
  /** Short-lived presigned S3 URL; may be absent. */
  thumbnailUrl?: string;
  screenshotStatus: ScreenshotStatus;
  processingStatus: ProcessingStatus;
  firstShare: FirstShare;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LinkDetail extends LinkSummary {
  /** Model-written paragraph on what the site is about. Absent unless an LLM provider ran. */
  tldr?: string;
  normalizedUrl: string;
  finalUrl?: string;
  canonicalUrl?: string;
  language?: string;
  contentType?: string;
  categoryConfidence: number;
  categoryManualOverride: boolean;
  autoCategories: string[];
  /** Presigned full-size screenshot URL. */
  screenshotUrl?: string;
  screenshotCapturedAt?: string;
  failureReason?: string;
  failureStage?: string;
  attempts: number;
}

export interface Category {
  categoryId: string;
  name: string;
  slug: string;
  description?: string;
  archived: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /**
   * Number of DISTINCT links carrying this category. Not part of the documented contract yet —
   * the category management screen renders it when the API sends it and silently omits it
   * otherwise, so shipping it on the backend needs no frontend change.
   */
  linkCount?: number;
}

export interface FacetValue {
  value: string;
  label: string;
  linkCount: number;
}

export interface QueueStats {
  available: number;
  inFlight: number;
}

/** One entry in a "top N" ranking. `linkCount` is DISTINCT links, never shares. */
export interface RankedFacet {
  value: string;
  label: string;
  linkCount: number;
}

/** One point on the monthly activity chart. */
export interface ActivityPoint {
  /** Calendar month as `YYYY-MM`. */
  period: string;
  count: number;
}

/** A month-by-month series of first shares. */
export interface ActivitySeries {
  granularity: 'month';
  points: ActivityPoint[];
  totalCounted: number;
  /** True when the catalogue exceeded the aggregation cap; the earliest months are undercounted. */
  truncated: boolean;
}

/**
 * Response of `GET /api/v1/stats`. Behind sign-in — every figure is derived from who shared what
 * and where. Counts are DISTINCT LINKS: "most shared" means the first sharer of the most unique
 * links, because SLC records no later-share history. Images and videos are counted the same way,
 * as DISTINCT FILES, in their own fields — `linkCount` on a media ranking is a count of files.
 */
export interface Stats {
  totals: {
    links: number;
    /** Images and videos together; `images + videos`. */
    media: number;
    images: number;
    videos: number;
    /** Channels with links, files or both — each counted once. */
    channels: number;
    /** People who first shared links, files or both — each counted once. */
    sharers: number;
    domains: number;
    categoriesInUse: number;
  };
  topChannels: RankedFacet[];
  topSharers: RankedFacet[];
  topDomains: RankedFacet[];
  topCategories: RankedFacet[];
  topMediaChannels: RankedFacet[];
  topMediaSharers: RankedFacet[];
  activity: ActivitySeries;
  mediaActivity: ActivitySeries;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface ItemsResponse<T> {
  items: T[];
}

export interface AdminFailuresResponse {
  enrichmentFailures: LinkSummary[];
  screenshotFailures: LinkSummary[];
  queues: {
    enrichment: QueueStats;
    screenshot: QueueStats;
    enrichmentDlq: QueueStats;
    screenshotDlq: QueueStats;
    /** Absent from an API deployed before media collection. */
    media?: QueueStats;
    mediaDlq?: QueueStats;
    /** Absent from an API deployed before media titles. */
    mediaDescribe?: QueueStats;
    mediaDescribeDlq?: QueueStats;
  };
}

export type LinkSortField = 'createdAt' | 'firstSharedAt' | 'title';
export type SortOrder = 'asc' | 'desc';

/** Exactly the query parameters `GET /api/v1/links` accepts. */
export interface ListLinksParams {
  query?: string;
  /** Repeatable. */
  category?: string[];
  domain?: string;
  sharerId?: string;
  channelId?: string;
  from?: string;
  to?: string;
  sort?: LinkSortField;
  order?: SortOrder;
  archived?: boolean;
  status?: ProcessingStatus;
  limit?: number;
  cursor?: string;
}

export interface UpdateLinkBody {
  title?: string;
  description?: string;
  categories?: string[];
  archived?: boolean;
}

/** Manual screenshot upload. `image` and `thumbnail` are base64-encoded bytes (no `data:` prefix). */
export interface UploadScreenshotBody {
  contentType: 'image/webp' | 'image/png' | 'image/jpeg';
  image: string;
  thumbnail: string;
}

export interface CreateCategoryBody {
  name: string;
  description?: string;
}

export interface UpdateCategoryBody {
  name?: string;
  description?: string;
  archived?: boolean;
  sortOrder?: number;
  /** Merging moves every link on this category onto `mergeIntoSlug`, then retires this one. */
  mergeIntoSlug?: string;
}

/** Error envelope returned by every non-2xx API response. */
export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    requestId: string;
    details?: unknown;
  };
}
